import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SEO from '../../components/SEO';
import Services from '../../services/Services';
import { getPublicToken } from '../../utils/publicToken';
import { generateCommunityUrl, generateEventUrl } from '../../utils/urlSlug';

type ShareRedirectStatus = 'loading' | 'error';

function RedirectShell({ title }: { title: string }) {
  return (
    <>
      <SEO title={title} noindex />
      <div
        style={{
          width: '100%',
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          color: 'var(--color-text-secondary, #6B7280)',
          fontFamily: 'Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        }}
      >
        {title}
      </div>
    </>
  );
}

export const ShareEventRedirectPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ShareRedirectStatus>('loading');

  const fallbackPath = useMemo(() => {
    if (!id) return '/events';
    return generateEventUrl(undefined, id);
  }, [id]);

  useEffect(() => {
    if (!id) {
      navigate('/events', { replace: true });
      return;
    }

    const abortController = new AbortController();

    const run = async () => {
      try {
        const token = (await getPublicToken()) || '';
        if (!token) {
          navigate(fallbackPath, { replace: true });
          return;
        }

        const response = await Services.SocialHiveGetEventDetails(token, id, abortController);
        const result = response.data?.result as any;

        const eventTitle: string | undefined = result?.eventTitle || result?.title;
        const eventID: string = String(result?.eventID || id);

        navigate(generateEventUrl(eventTitle, eventID), { replace: true });
      } catch (err: any) {
        // Abort = user navigated away; do nothing.
        if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
        setStatus('error');
        navigate(fallbackPath, { replace: true });
      }
    };

    void run();

    return () => abortController.abort();
  }, [fallbackPath, id, navigate]);

  return <RedirectShell title={status === 'error' ? 'Redirecting…' : 'Redirecting…'} />;
};

export const ShareCommunityRedirectPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ShareRedirectStatus>('loading');

  const fallbackPath = useMemo(() => {
    if (!id) return '/communities';
    return generateCommunityUrl(undefined, id);
  }, [id]);

  useEffect(() => {
    if (!id) {
      navigate('/communities', { replace: true });
      return;
    }

    const abortController = new AbortController();

    const run = async () => {
      try {
        const token = (await getPublicToken()) || '';
        if (!token) {
          navigate(fallbackPath, { replace: true });
          return;
        }

        const response = await Services.SocialHiveGetChannel(token, id, abortController);
        const result = response.data?.result as any;

        const channelTitle: string | undefined = result?.channelTitle || result?.title;
        const channelID: string = String(result?.channelID || id);

        navigate(generateCommunityUrl(channelTitle, channelID), { replace: true });
      } catch (err: any) {
        // Abort = user navigated away; do nothing.
        if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
        setStatus('error');
        navigate(fallbackPath, { replace: true });
      }
    };

    void run();

    return () => abortController.abort();
  }, [fallbackPath, id, navigate]);

  return <RedirectShell title={status === 'error' ? 'Redirecting…' : 'Redirecting…'} />;
};

